import React, { useMemo } from 'react';
import {
  CustomSlotConfig,
  WEEKDAYS,
} from '../../CustomBlocks/slotBooking/slotTypes';
import { getTimezoneOptions } from '../../CustomBlocks/slotBooking/slotUtils';
import { AlertIcon, CalendarClockIcon, CalendarIcon, ClockIcon, GlobeIcon, MinusIcon } from '../../shared/Icons';
import { NumberStepper, SettingsGroup, SettingsRow } from './settingsUi';
import { IconTile, SummaryCard, TILE } from './SlotBookingEditor.styles';
import WeeklyHoursEditor from './WeeklyHoursEditor';
import BlockedDatesEditor from './BlockedDatesEditor';

interface CustomSourceSectionProps {
  value: CustomSlotConfig;
  onChange: (patch: Partial<CustomSlotConfig>) => void;
}

const describeDays = (config: CustomSlotConfig): string => {
  const enabled = WEEKDAYS.filter(({ key }) => config.weeklyHours[key].enabled);
  if (!enabled.length) return 'No available days';
  if (enabled.length === 7) return 'Every day';
  const indexes = enabled.map((day) => WEEKDAYS.indexOf(day));
  const contiguous = indexes.every((idx, i) => i === 0 || idx === indexes[i - 1] + 1);
  const short = (label: string) => label.slice(0, 3);
  return contiguous && enabled.length > 2
    ? `${short(enabled[0].label)} – ${short(enabled[enabled.length - 1].label)}`
    : enabled.map((day) => short(day.label)).join(', ');
};

const RULES: {
  key: 'durationMinutes' | 'bufferMinutes' | 'minNoticeHours' | 'maxDaysAhead';
  label: string;
  description: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  color: string;
  icon: React.ReactNode;
}[] = [
  { key: 'durationMinutes', label: 'Duration', description: 'Length of each slot', unit: 'min', min: 5, max: 480, step: 5, color: TILE.indigo, icon: <ClockIcon size={16} /> },
  { key: 'bufferMinutes', label: 'Buffer', description: 'Break after each slot', unit: 'min', min: 0, max: 240, step: 5, color: TILE.teal, icon: <MinusIcon size={16} /> },
  { key: 'minNoticeHours', label: 'Minimum notice', description: 'Hide slots starting sooner', unit: 'hr', min: 0, max: 720, step: 1, color: TILE.orange, icon: <AlertIcon size={16} /> },
  { key: 'maxDaysAhead', label: 'Booking window', description: 'Days ahead · 0 = no limit', unit: 'days', min: 0, max: 365, step: 1, color: TILE.green, icon: <CalendarIcon size={16} /> },
];

const CustomSourceSection: React.FC<CustomSourceSectionProps> = ({ value, onChange }) => {
  const timezones = useMemo(getTimezoneOptions, []);

  return (
    <>
      <SummaryCard>
        <IconTile $color={TILE.indigo}>
          <CalendarClockIcon size={16} />
        </IconTile>
        <div>
          <div className="sb-summary-title">{describeDays(value)}</div>
          <div className="sb-summary-meta">
            {value.durationMinutes}-min slots
            {value.minNoticeHours ? ` · ${value.minNoticeHours} hr notice` : ''}
            {value.maxDaysAhead ? ` · up to ${value.maxDaysAhead} days ahead` : ''}
          </div>
        </div>
      </SummaryCard>

      <SettingsGroup footer="Weekly hours are in this timezone. Respondents see slots in their own.">
        <SettingsRow icon={<GlobeIcon size={16} />} iconColor={TILE.blue} label="Host timezone">
          <select
            className="sb-plain-select"
            value={value.timezone}
            onChange={(e) => onChange({ timezone: e.target.value })}
            aria-label="Host timezone"
          >
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </SettingsRow>
      </SettingsGroup>

      <WeeklyHoursEditor
        value={value.weeklyHours}
        onChange={(weeklyHours) => onChange({ weeklyHours })}
      />

      <SettingsGroup title="Booking rules">
        {RULES.map((rule) => (
          <SettingsRow
            key={rule.key}
            icon={rule.icon}
            iconColor={rule.color}
            label={rule.label}
            description={rule.description}
          >
            <NumberStepper
              value={Number(value[rule.key]) || 0}
              min={rule.min}
              max={rule.max}
              step={rule.step}
              unit={rule.unit}
              label={rule.label}
              onChange={(next) => onChange({ [rule.key]: next })}
            />
          </SettingsRow>
        ))}
      </SettingsGroup>

      <BlockedDatesEditor
        value={value.blockedDates}
        onChange={(blockedDates) => onChange({ blockedDates })}
      />
    </>
  );
};

export default CustomSourceSection;
